<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAppStore } from '@/stores/app'
import { useTr } from '@/composables/useTr'
import { ICONS } from '@/composables/useIcons'
import { navMain } from '@/data/nav'

const app = useAppStore()
const route = useRoute()
const router = useRouter()
const { tr, locale } = useTr()

const createOpen = ref(true)
const currentPage = computed(() => route.meta?.pageId as string | undefined)

function go(name: string) {
  router.push({ name })
}

function isSettingsActive(): boolean {
  const p = currentPage.value
  if (!p) return false
  return p === 'settings' || p.startsWith('s-') || p === 'integrations'
}
</script>

<template>
  <aside class="sidebar" :class="{ collapsed: app.sidebarCollapsed }">
    <div class="sb-head">
      <a class="sb-logo" @click="go('dashboard')">
        <div class="sb-logo-mark">G</div>
        <span v-if="!app.sidebarCollapsed" class="name">GhostMap-CN</span>
      </a>
      <div class="sb-collapse" @click="app.sidebarCollapsed = !app.sidebarCollapsed" :title="app.sidebarCollapsed ? tr('展开', 'Expand') : tr('折叠', 'Collapse')">
        <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18"/></svg>
      </div>
    </div>

    <div v-if="!app.sidebarCollapsed" class="team-switch">
      <div class="avatar-sm">D</div>
      <div class="label">{{ tr('您的工作区', 'Your Workspace') }}</div>
      <svg class="arrows" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M7 9l5-5 5 5M7 15l5 5 5-5"/></svg>
    </div>

    <div>
      <button class="create-btn" @click="createOpen = !createOpen">
        <svg class="icon-plus" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg>
        <span v-if="!app.sidebarCollapsed" class="label">{{ tr('新建', 'Create') }}</span>
        <svg v-if="!app.sidebarCollapsed" class="arrows" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      <div v-if="createOpen && !app.sidebarCollapsed" class="create-nest">
        <div class="sb-item" @click="go('targets')">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.5"/></svg>
          <span class="label">{{ tr('资产侦察', 'Asset Discovery') }}</span>
        </div>
        <div class="sb-item">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M12 2L4 6v6c0 5 3.5 9 8 10 4.5-1 8-5 8-10V6l-8-4z"/><path d="M9 12l2 2 4-4"/></svg>
          <span class="label">{{ tr('漏洞扫描', 'Vulnerability Scan') }}</span>
        </div>
        <div class="sb-item">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span class="label">{{ tr('扫描模板', 'Scan Templates') }}</span>
        </div>
        <div class="sb-item" @click="app.proModal = true">
          <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><polyline points="14 2 14 8 20 8"/><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM9 13h6M9 17h3"/></svg>
          <span class="label">{{ tr('AI 报告草稿', 'AI Report Draft') }}</span>
          <svg class="lock" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
      </div>
    </div>

    <nav class="sb-nav">
      <div class="group-label">{{ tr('总览', 'OVERVIEW') }}</div>
      <div
        v-for="item in navMain"
        :key="item.id"
        class="sb-item"
        :class="{ active: currentPage === item.id }"
        @click="go(item.id)"
      >
        <span v-html="ICONS[item.iconKey]" />
        <span class="label">{{ locale === 'en' ? item.labelEn : item.label }}</span>
        <span v-if="item.badge" class="badge num">{{ item.badge }}</span>
      </div>

      <div class="group-label">{{ tr('交付', 'DELIVERABLES') }}</div>
      <div class="sb-item" :class="{ active: currentPage === 'reports' }" @click="go('reports')">
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
        <span class="label">{{ tr('审计报告', 'Reports') }}</span>
      </div>
    </nav>

    <div class="sb-foot">
      <div class="sb-item" :class="{ active: isSettingsActive() }" @click="go('s-workspace')">
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
        <span class="label">{{ tr('设置', 'Settings') }}</span>
      </div>
      <div v-show="app.advancedMode || app.isPro" class="sb-item" @click="app.devApiModal = true">
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>
        <span class="label">{{ tr('开发者接口', 'Developer API') }}</span>
        <span v-show="!app.isPro" class="lock" style="font-size:11px;">{{ tr('🔧 高级', '🔧 Advanced') }}</span>
      </div>
      <div
        class="sb-item"
        @click="app.advancedMode = !app.advancedMode"
        :title="tr('打开后显示开发者接口 / 系统状态 / 操作日志 (面向自部署 / 高级用户)', 'When on, shows Developer API / System Status / Audit Log (for self-hosted / advanced users)')"
      >
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/></svg>
        <span class="label">{{ tr('高级模式', 'Advanced Mode') }}</span>
        <span class="badge" :style="app.advancedMode ? 'color:var(--text); background:var(--panel-soft);' : ''">
          {{ app.advancedMode ? tr('开', 'ON') : tr('关', 'OFF') }}
        </span>
      </div>
      <div class="sb-item" :class="{ active: currentPage === 'help' }" @click="go('help')">
        <svg fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M9 9a3 3 0 016 0c0 2-3 2-3 4M12 17h.01"/></svg>
        <span class="label">{{ tr('帮助中心', 'Help') }}</span>
      </div>
    </div>
  </aside>
</template>
